import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import { VALUE_CHAIN_SECTION } from 'constants/formFields';
import { useEnumQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums } from 'store/slices/dataSets/selectors';
import { useGetProductDataQuery, useUidNameTuplesVCQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { selectUidNameTuplesVC } from 'store/slices/valueChainMap/selectors';
import getProductDefaultValues from 'utils/formHelpers/getProductDefaultValues';
import { createProductModalSchema } from 'utils/validators';

import type { TCreateProductSchema } from 'utils/validators';
import type ICreateProductForm from '../interfaces/ICreateProductForm';

const useCreateProductForm = ({
  recordId, isPageLoading, valueChainMapId, valueChainSectionCurrent, productCategoriesOptions,
}: ICreateProductForm) => {
  const { data } = useGetProductDataQuery(
    recordId ? { chainId: `${recordId}` } : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  const { data: enumData } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.volumeQtyUnit,
      DATA_SETS_ENUM_TYPES.relianceOnFreeNaturalResources, DATA_SETS_ENUM_TYPES.relianceOnFreeSocialResources,
      DATA_SETS_ENUM_TYPES.criticalityRating],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: profitCenterIds } = useUidNameTuplesVCQuery({
    types: [UID_NAME_TUPLES.profitCenter],
    valueChainMapId,
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuplesVC(result.data) }),
  });

  const {
    control,
    handleSubmit,
    formState,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
  } = useForm<TCreateProductSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(createProductModalSchema),
    values: isPageLoading ? undefined : getProductDefaultValues({
      values: data?.data,
      enumData,
      valueChainSectionCurrent,
      productCategoriesOptions,
    }),
  });

  useEffect(() => {
    if (valueChainSectionCurrent && !isPageLoading) {
      setValue(VALUE_CHAIN_SECTION, {
        value: valueChainSectionCurrent,
        label: valueChainSectionCurrent,
      });
    }
  }, [valueChainSectionCurrent, setValue, isPageLoading]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
  };
};

export default useCreateProductForm;
